import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { NumberField } from '#client/components/ui/NumberField';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { formatMoney } from '#client/lib/format';
import {
  type DeliveryHeader,
  type DeliveryLine,
  calculateInvoiceValue,
  outstandingPacks,
} from './receive-stock.logic';
import type { SetField } from '#client/hooks/useFormValues';

interface DeliveryHeaderCardProps {
  values: DeliveryHeader;
  onFieldChange: SetField<DeliveryHeader>;
}

export function DeliveryHeaderCard({ values, onFieldChange }: DeliveryHeaderCardProps) {
  return (
    <Card title="The delivery">
      <div className="c-form-grid">
        <TextField
          label="Arrived on"
          type="date"
          value={values.received_at}
          onChange={(event) => onFieldChange('received_at', event.target.value)}
        />
        <TextField
          label="Invoice number"
          value={values.invoice_no}
          onChange={(event) => onFieldChange('invoice_no', event.target.value)}
        />
        <TextField
          label="Delivery note"
          value={values.delivery_note}
          onChange={(event) => onFieldChange('delivery_note', event.target.value)}
        />
      </div>
      <TextArea
        label="Note"
        rows={2}
        value={values.note}
        onChange={(event) => onFieldChange('note', event.target.value)}
      />
    </Card>
  );
}

interface DeliveryLinesCardProps {
  lines: readonly DeliveryLine[];
  onChange: (lineId: number, changes: Partial<DeliveryLine>) => void;
}

export function DeliveryLinesCard({ lines, onChange }: DeliveryLinesCardProps) {
  const included = lines.filter((line) => line.include);
  const invoiceValue = calculateInvoiceValue(included);

  return (
    <Card
      title="What arrived"
      subtitle="Pre-filled with what is still outstanding. Untick anything that did not come."
    >
      <ul className="c-order-lines">
        {lines.map((line) => (
          <DeliveryLineRow
            key={line.line_id}
            line={line}
            onChange={(changes) => onChange(line.line_id, changes)}
          />
        ))}
      </ul>

      {included.length > 0 && (
        <dl className="c-summary">
          <div className="c-summary__row">
            <dt>Reagents on this delivery</dt>
            <dd>{included.length}</dd>
          </div>
          {invoiceValue > 0 && (
            <div className="c-summary__row c-summary__row--total">
              <dt>Invoice value</dt>
              <dd>{formatMoney(invoiceValue)}</dd>
            </div>
          )}
        </dl>
      )}
    </Card>
  );
}

interface DeliveryLineRowProps {
  line: DeliveryLine;
  onChange: (changes: Partial<DeliveryLine>) => void;
}

function DeliveryLineRow({ line, onChange }: DeliveryLineRowProps) {
  const outstanding = outstandingPacks(line);

  return (
    <li className="c-order-line c-order-line--receive">
      <Checkbox
        label={line.label}
        checked={line.include}
        onChange={(event) => onChange({ include: event.target.checked })}
      />
      <p className="c-order-line__stock">
        {line.ordered_packs} ordered · {line.received_packs} already arrived ·{' '}
        {outstanding === 0 ? (
          <strong className="c-requisition__settled">nothing outstanding</strong>
        ) : (
          <strong>{outstanding} outstanding</strong>
        )}
      </p>

      {line.include && (
        <div className="c-form-grid">
          <NumberField
            label="Packs"
            min={1}
            value={line.quantity_packs}
            onChange={(packs) => onChange({ quantity_packs: packs })}
            required
          />
          <TextField
            label="Lot number"
            value={line.lot_no}
            onChange={(event) => onChange({ lot_no: event.target.value })}
            helpText="Use — for a consumable with no vendor batch"
            required
          />
          <TextField
            label="Expiry"
            type="date"
            value={line.expiry_at}
            onChange={(event) => onChange({ expiry_at: event.target.value })}
            helpText="Leave blank if it does not expire"
          />
          <NumberField
            label="Price per pack"
            min={0}
            step={0.01}
            value={line.pack_price}
            onChange={(price) => onChange({ pack_price: price })}
            prefix="Rs"
            helpText="As invoiced. The per-unit cost is worked out from the pack size."
          />
        </div>
      )}
    </li>
  );
}
